import type { PatternContent } from './types'

const T = { label: 'True' }
const F = { label: 'False' }

export const BEHAVIOURAL: PatternContent[] = [
  {
    slug: 'mediator',
    teach: [
      {
        label: 'The problem',
        text: 'Ten components that all know about each other form a web. Change one and you re-test the rest, because every link is a hand-written dependency.',
      },
      {
        label: 'The shape',
        text: 'Each component talks only to one coordinator. The coordinator knows who should hear what and routes the message, so parts never name each other.',
      },
      {
        label: 'A trap',
        text: 'The coordinator can swallow every rule and become a god object. Route and translate in it; keep the real work inside the components.',
      },
    ],
    whenToUse:
      'Many peers need to react to one another and the direct links have turned into a tangle you cannot change safely.',
    whenNotTo:
      'Two or three parts that talk simply. A hub there is a detour, and it tends to grow into the one place nobody dares touch.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'A Kubernetes API server that controllers read and write through, never calling each other, is a Mediator.',
        choices: [{ ...T, correct: true }, F],
        explanation:
          'Controllers only know the hub, which routes state between them, so they never couple directly.',
      },
      {
        format: 'judge',
        prompt: 'A service that calls three other services by name in turn is a Mediator.',
        choices: [T, { ...F, correct: true }],
        explanation:
          'That is plain orchestration code; a Mediator is what peers go through so they need not know each other.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does a Mediator remove from the components around it?',
        choices: [
          { label: 'Knowledge of each other', correct: true },
          { label: 'Their own state' },
          { label: 'The need for an interface' },
          { label: 'Any way of being tested' },
        ],
        explanation: 'Components keep their state; they just stop referencing their peers directly.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: 'type Hub struct{ db *DB; mail *Mailer }\n\nfunc (h *Hub) Notify(from string, e Event) {\n\tif e.Kind == "paid" {\n\t\th.db.MarkPaid(e.ID)\n\t\th.mail.Send(e.ID, taxText(e))\n\t\th.db.Ledger(e.ID, e.Amount*1.2)\n\t}\n}',
        choices: [
          { label: 'Mediator' },
          { label: 'Observer' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation:
          'The hub holds tax and payment rules itself: a Mediator that routes became a god object.',
      },
    ],
  },
  {
    slug: 'memento',
    teach: [
      {
        label: 'The problem',
        text: 'You want undo, or a way back from a failed step, but the object guards its fields. Reaching in to copy them breaks the guard.',
      },
      {
        label: 'The shape',
        text: 'The object makes a snapshot of itself and hands it out as an opaque token. Later it takes the token back and restores itself from it.',
      },
      {
        label: 'A trap',
        text: 'Snapshots cost memory. Taking one per keystroke of a big object adds up fast, so store a delta or cap how many you keep.',
      },
    ],
    whenToUse:
      'You need rollback or undo of an object whose insides should stay private, and the state is small enough to copy.',
    whenNotTo:
      'The state is huge or changes constantly. Log the changes instead of whole snapshots, or the memory bill gets out of hand.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          "Saving an Eloquent model's getOriginal() so you can compare or restore later is Memento-like.",
        choices: [{ ...T, correct: true }, F],
        explanation:
          'It is a captured copy of earlier state, taken so the model can be checked against or put back.',
      },
      {
        format: 'judge',
        prompt: 'A Memento lets any caller read and edit the saved fields directly.',
        choices: [T, { ...F, correct: true }],
        explanation:
          'The snapshot is opaque; only the originating object reads it, otherwise encapsulation is lost.',
      },
      {
        format: 'quick_pick',
        prompt: 'Who should be able to read what is inside a snapshot?',
        choices: [
          { label: 'Only the object that made it', correct: true },
          { label: 'Every caller holding it' },
          { label: 'The database layer' },
          { label: 'Whoever restores it last' },
        ],
        explanation: 'The caretaker stores it blind; only the originator knows how to unpack it.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: 'type Editor struct{ text string }\n\ntype snapshot struct{ text string }\n\nfunc (e *Editor) Save() snapshot {\n\treturn snapshot{e.text}\n}\n\nfunc (e *Editor) Restore(s snapshot) {\n\te.text = s.text\n}',
        choices: [
          { label: 'Command' },
          { label: 'None of these' },
          { label: 'Memento', correct: true },
          { label: 'Misused' },
        ],
        explanation:
          'The editor emits its own opaque snapshot and restores from it; callers just hold the token.',
      },
    ],
  },
  {
    slug: 'observer',
    teach: [
      {
        label: 'The problem',
        text: 'When an order is paid, mail, stock and analytics all need to know. Calling each from the order makes it depend on all of them.',
      },
      {
        label: 'The shape',
        text: 'The subject keeps a list of subscribers and notifies them on a change. It does not know what they do, and they can come and go.',
      },
      {
        label: 'A trap',
        text: 'Listeners that must run in a set order, or that change what the caller needs next, hide the real flow. Surprises hide in the wiring.',
      },
    ],
    whenToUse:
      'One change should trigger several independent reactions, and the source should not know or care who is listening.',
    whenNotTo:
      'The step is required for the result to be right. Call it directly so the order and the failure path are in plain view.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'Laravel dispatching OrderPaid, with SendReceipt and UpdateStock listening, is an Observer.',
        choices: [{ ...T, correct: true }, F],
        explanation:
          'The event source does not know its listeners; each one subscribes and reacts on its own.',
      },
      {
        format: 'judge',
        prompt: 'A controller that calls $mailer->send() then $stock->drop() in turn is an Observer.',
        choices: [T, { ...F, correct: true }],
        explanation: 'Those are direct calls to known collaborators; nothing subscribes or gets notified.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does the subject in an Observer know about its subscribers?',
        choices: [
          { label: 'Only that they can be notified', correct: true },
          { label: 'Their concrete classes' },
          { label: 'Which order they must run in' },
          { label: 'What they do with the data' },
        ],
        explanation:
          'That narrow knowledge is the point; the subject stays decoupled from what listeners do.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: "Event::listen('saved', function ($o) {\n    $o->total = $o->total * 1.2;\n});\nEvent::listen('saved', function ($o) {\n    $o->total = round($o->total);\n});\n\n$o->save();\ncharge($o->total);",
        choices: [
          { label: 'Observer' },
          { label: 'Mediator' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation:
          'Charging depends on listeners running in order; required logic hides behind a global bus.',
      },
    ],
  },
  {
    slug: 'state',
    teach: [
      {
        label: 'The problem',
        text: 'A task behaves differently as inbox, next, waiting or done. One class with if-chains on a status field grows a branch for every rule.',
      },
      {
        label: 'The shape',
        text: 'Each state is its own class with the same methods. The object holds the current one and hands calls to it, and a state names the next state.',
      },
      {
        label: 'A trap',
        text: 'With two states and one rule, an enum and a match is clearer. State pays off once behaviour, not just a label, differs per state.',
      },
    ],
    whenToUse:
      'Behaviour changes with a mode and the if-chains on that mode are spreading through several methods.',
    whenNotTo:
      'The mode is only a label with one or two rules. A plain enum with a match is shorter and easier to read.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'Task states as classes that run done() and return the next state object is the State pattern.',
        choices: [{ ...T, correct: true }, F],
        explanation: 'The state object itself decides the transition and returns the next state.',
      },
      {
        format: 'judge',
        prompt: 'A Go func chosen by the caller to price an order is State, not Strategy.',
        choices: [T, { ...F, correct: true }],
        explanation:
          'The caller picks an algorithm: that is Strategy. State switches itself as the object changes.',
      },
      {
        format: 'quick_pick',
        prompt: 'In State, who normally decides which state comes next?',
        choices: [
          { label: 'The current state object', correct: true },
          { label: 'The client, every call' },
          { label: 'A config file' },
          { label: 'A shared base class' },
        ],
        explanation: 'The states carry the transition rules, so the context needs no growing if-chain.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: 'interface State {\n    public function done(Task $t): State;\n}\nclass Waiting implements State {\n    public function done(Task $t): State {\n        $t->closedAt = now();\n        return new Done();\n    }\n}',
        choices: [
          { label: 'Strategy' },
          { label: 'State', correct: true },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation:
          'Waiting does its own work and returns the next state, so the task changes behaviour on its own.',
      },
    ],
  },
  {
    slug: 'strategy',
    teach: [
      {
        label: 'The problem',
        text: 'Pricing, routing or storage has several ways to do the same job, and a switch on a type name keeps growing as new ways are added.',
      },
      {
        label: 'The shape',
        text: 'Put each way behind one signature, in Go often just a func type. The caller passes in the one it wants, and the host never branches.',
      },
      {
        label: 'A trap',
        text: 'Wrapping a single algorithm in an interface with one class is noise. Wait until there are two real variants before adding the seam.',
      },
    ],
    whenToUse:
      'The same job has several interchangeable methods, picked per tenant, per config or at runtime.',
    whenNotTo:
      'There is one way and no sign of a second. A bare function is simpler than a family of classes you never swap.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'Passing a less func to sort.Slice, so the same sorter orders by name or by date, is Strategy.',
        choices: [{ ...T, correct: true }, F],
        explanation:
          'The ordering rule is a swappable function; sort.Slice keeps one algorithm and takes the rule.',
      },
      {
        format: 'judge',
        prompt: 'An object that returns the next object and so alters how it behaves is Strategy.',
        choices: [T, { ...F, correct: true }],
        explanation: 'That is State: it moves on by itself. In Strategy the client picks, and it stays put.',
      },
      {
        format: 'quick_pick',
        prompt: 'Which problem does Strategy fix best?',
        choices: [
          { label: 'Switch growing with each variant', correct: true },
          { label: 'Undoing the last change' },
          { label: 'Telling many parts about an event' },
          { label: 'Walking a tree of nodes' },
        ],
        explanation:
          'Each way becomes a swappable unit, so adding one means adding code, not editing a switch.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: 'type Pricer func(qty int) int\n\nfunc Bulk(q int) int { return q * 8 }\nfunc Retail(q int) int { return q * 10 }\n\nfunc Total(q int, p Pricer) int {\n\treturn p(q)\n}',
        choices: [
          { label: 'Strategy', correct: true },
          { label: 'State' },
          { label: 'Misused' },
          { label: 'None of these' },
        ],
        explanation:
          'Two pricing rules share one signature and the caller hands one in; Total never branches.',
      },
    ],
  },
  {
    slug: 'template-method',
    teach: [
      {
        label: 'The problem',
        text: 'Several classes follow the same steps: check, validate, run, clean up. Copying the order into each lets the copies drift apart.',
      },
      {
        label: 'The shape',
        text: 'A base class owns the skeleton and calls named hooks in a fixed order. Subclasses fill in just the steps that differ, never the order.',
      },
      {
        label: 'A trap',
        text: 'It leans on inheritance, so one subclass cannot mix two skeletons. If the steps vary a lot, pass them in as funcs instead.',
      },
    ],
    whenToUse:
      'Many variants share the same sequence of steps and only a few of those steps differ between them.',
    whenNotTo:
      'The sequence itself varies, or you want to combine behaviours. Compose small funcs or objects rather than inherit.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'A Laravel FormRequest, where you override authorize() and rules() and the framework runs them, is a Template Method.',
        choices: [{ ...T, correct: true }, F],
        explanation: 'The framework fixes the validation flow and calls your hooks at set points.',
      },
      {
        format: 'judge',
        prompt: 'A base class with one abstract method and no fixed sequence of steps is a Template Method.',
        choices: [T, { ...F, correct: true }],
        explanation: 'Without a skeleton calling hooks in a set order, that is just plain polymorphism.',
      },
      {
        format: 'quick_pick',
        prompt: 'In Template Method, what must a subclass NOT be able to change?',
        choices: [
          { label: 'The order of the steps', correct: true },
          { label: 'How a hook works' },
          { label: 'Its own fields' },
          { label: 'Its class name' },
        ],
        explanation: 'The base class keeps the skeleton fixed so subclasses can only fill in the blanks.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: "abstract class Report {\n    abstract function render(): string;\n}\n\nclass Sales extends Report {\n    public function render(): string {\n        return 'sales';\n    }\n}",
        choices: [
          { label: 'Template Method' },
          { label: 'Strategy' },
          { label: 'None of these', correct: true },
          { label: 'Misused' },
        ],
        explanation:
          'No skeleton calls steps in order; it is one overridden method, so just plain inheritance.',
      },
    ],
  },
  {
    slug: 'visitor',
    teach: [
      {
        label: 'The problem',
        text: 'You have a tree of node types and keep wanting new operations on it: find calls, rename, count. Editing every node class each time hurts.',
      },
      {
        label: 'The shape',
        text: 'Each node has one accept method that hands itself to a visitor. A new operation is a new visitor class, and the nodes never change.',
      },
      {
        label: 'A trap',
        text: 'Adding a new node type means touching every visitor. It suits a stable set of types with a growing set of operations, not the reverse.',
      },
    ],
    whenToUse:
      'A stable set of types needs many different operations, such as tools that walk a syntax tree.',
    whenNotTo:
      'You add new types often. Every visitor has to learn about each one, so a plain method on the type is cheaper.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'A PHPStan or Rector rule that extends NodeVisitorAbstract and runs enterNode() on each node is a Visitor.',
        choices: [{ ...T, correct: true }, F],
        explanation:
          'The walker visits each node and the rule adds an operation without editing node classes.',
      },
      {
        format: 'judge',
        prompt: 'Visitor makes it cheap to add a new node type, since no visitor needs to change.',
        choices: [T, { ...F, correct: true }],
        explanation:
          'It is the reverse: new operations are cheap, but each new node type touches every visitor.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does a Visitor let you add without editing the node classes?',
        choices: [
          { label: 'New operations over them', correct: true },
          { label: 'New node types' },
          { label: 'New fields on each node' },
          { label: 'Undo for each node' },
        ],
        explanation: 'The operation lives in the visitor, so the node classes stay as they are.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: 'class FindCalls extends NodeVisitor {\n    public array $calls = [];\n    public function enterNode(Node $n) {\n        if ($n instanceof FuncCall) {\n            $this->calls[] = $n;\n        }\n        return null;\n    }\n}',
        choices: [
          { label: 'Iterator' },
          { label: 'Visitor', correct: true },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'A separate class carries the operation and the walker hands each node to it.',
      },
    ],
  },
]
